import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useProject } from '../contexts/ProjectContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const { FiDownload, FiUpload, FiX, FiFolder, FiFile, FiCheck, FiAlertCircle } = FiIcons;

const ProjectImportExport = ({ onClose }) => {
  const { projects, currentProject, files, createProjectWithFiles } = useProject();
  const [dragOver, setDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState(null);

  const exportProject = async (project) => {
    const zip = new JSZip();

    // Add project metadata
    zip.file('project.json', JSON.stringify({
      name: project.name,
      template: project.template,
      createdAt: project.createdAt,
      exportedAt: new Date().toISOString()
    }, null, 2));

    // Add all files
    Object.entries(project.files).forEach(([filename, content]) => {
      zip.file(filename, content);
    });

    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, `${project.name}.zip`);
  };

  const exportCurrentProject = async () => {
    if (!currentProject) return;
    const projectToExport = { ...currentProject, files };
    await exportProject(projectToExport);
  };

  const handleFileUpload = async (file) => {
    setImporting(true);
    setImportStatus(null);

    try {
      const zip = new JSZip();
      const contents = await zip.loadAsync(file);
      console.log('📦 ZIP contents:', Object.keys(contents.files));

      // Check for project.json file
      const projectFile = contents.files['project.json'];
      let projectData = {
        name: file.name.replace('.zip', ''),
        template: 'blank',
        createdAt: new Date().toISOString()
      };

      if (projectFile) {
        try {
          const projectJson = await projectFile.async('text');
          projectData = { ...projectData, ...JSON.parse(projectJson) };
        } catch (error) {
          console.warn('Could not parse project.json, using defaults');
        }
      }

      const importedFiles = {};
      let fileCount = 0;

      // Read all files except project.json and directories
      for (const [filename, zipEntry] of Object.entries(contents.files)) {
        // Skip directories and project.json
        if (zipEntry.dir || filename === 'project.json') {
          continue;
        }

        // Skip hidden files and system files
        if (filename.startsWith('__MACOSX/') || filename.startsWith('.DS_Store')) {
          continue;
        }

        try {
          // Clean up the filename (remove any leading directory paths if needed)
          let cleanFilename = filename;
          // If the file is in a subdirectory, preserve the directory structure
          // but make sure it doesn't start with the project name folder
          const pathParts = filename.split('/');
          if (pathParts.length > 1) {
            // Check if first part might be project folder name
            const firstPart = pathParts[0];
            if (firstPart === projectData.name || firstPart === projectData.name.replace(/\s+/g, '-')) {
              // Remove the project folder prefix
              cleanFilename = pathParts.slice(1).join('/');
            }
          }

          // Skip empty filenames
          if (!cleanFilename) continue;

          const fileContent = await zipEntry.async('text');
          importedFiles[cleanFilename] = fileContent;
          fileCount++;
          console.log(`📄 Imported file: ${cleanFilename} (${fileContent.length} chars)`);
        } catch (error) {
          console.warn(`Could not read file ${filename}:`, error);
        }
      }

      // Ensure we have at least one file
      if (fileCount === 0) {
        // Create a basic index.html if no files were imported
        importedFiles['index.html'] = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${projectData.name}</title>
</head>
<body>
  <h1>Imported Project: ${projectData.name}</h1>
  <p>This project was imported but contained no readable files.</p>
</body>
</html>`;
        fileCount = 1;
      }

      console.log('🚀 Creating imported project with files:', Object.keys(importedFiles));

      // Use the enhanced createProjectWithFiles function that properly handles multiple files
      const newProject = await createProjectWithFiles(
        `${projectData.name} (Imported)`,
        projectData.template || 'blank',
        importedFiles // Pass all imported files at once
      );

      console.log('✅ Project created with all files:', Object.keys(newProject.files));

      setImportStatus({
        type: 'success',
        message: `Successfully imported ${fileCount} file(s)!`,
        details: Object.keys(importedFiles)
      });

      // Auto-close after 3 seconds on success
      setTimeout(() => {
        onClose();
      }, 3000);

    } catch (error) {
      console.error('❌ Import error:', error);
      setImportStatus({
        type: 'error',
        message: 'Failed to import project. Please check the file format.',
        details: [error.message]
      });
    } finally {
      setImporting(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const zipFile = droppedFiles.find(file => file.name.toLowerCase().endsWith('.zip'));

    if (zipFile) {
      handleFileUpload(zipFile);
    } else {
      setImportStatus({
        type: 'error',
        message: 'Please drop a .zip file',
        details: ['Only ZIP archives are supported for import']
      });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        className="bg-gray-800 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Import / Export Projects</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Export Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <SafeIcon icon={FiDownload} className="mr-2" />
              Export Projects
            </h3>

            {/* Current Project */}
            {currentProject && (
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-300 mb-2">Current Project</h4>
                <div className="bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <SafeIcon icon={FiFolder} className="text-blue-400 mr-3" />
                    <div>
                      <div className="text-white font-medium">{currentProject.name}</div>
                      <div className="text-sm text-gray-400">
                        {Object.keys(files).length} files
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={exportCurrentProject}
                    className="flex items-center px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                  >
                    <SafeIcon icon={FiDownload} className="mr-1 text-sm" />
                    Export
                  </button>
                </div>
              </div>
            )}

            {/* All Projects */}
            <div>
              <h4 className="text-md font-medium text-gray-300 mb-2">All Projects</h4>
              {projects.length === 0 ? (
                <div className="text-gray-400 text-center py-4">
                  No projects to export
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {projects.map((project) => (
                    <div
                      key={project.id}
                      className="bg-gray-700 rounded-lg p-3 flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <SafeIcon icon={FiFolder} className="text-blue-400 mr-3" />
                        <div>
                          <div className="text-white text-sm">{project.name}</div>
                          <div className="text-xs text-gray-400">
                            {Object.keys(project.files || {}).length} files
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => exportProject(project)}
                        className="flex items-center px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded transition-colors text-sm"
                      >
                        <SafeIcon icon={FiDownload} className="mr-1 text-xs" />
                        Export
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Import Section */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <SafeIcon icon={FiUpload} className="mr-2" />
              Import Project
            </h3>

            {/* Import Status */}
            {importStatus && (
              <div className={`mb-4 p-4 rounded-lg border ${
                importStatus.type === 'success' 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-red-500/10 border-red-500/30'
              }`}>
                <div className="flex items-center mb-2">
                  <SafeIcon 
                    icon={importStatus.type === 'success' ? FiCheck : FiAlertCircle} 
                    className={`mr-2 ${importStatus.type === 'success' ? 'text-green-400' : 'text-red-400'}`} 
                  />
                  <span className={`font-medium ${importStatus.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>
                    {importStatus.message}
                  </span>
                </div>
                {importStatus.details && importStatus.details.length > 0 && (
                  <div className="ml-6 text-sm">
                    {importStatus.type === 'success' ? (
                      <div>
                        <p className="text-green-200 mb-1">Imported files:</p>
                        <ul className="list-disc list-inside text-green-300 space-y-1">
                          {importStatus.details.slice(0, 8).map((file, index) => (
                            <li key={index} className="text-xs">{file}</li>
                          ))}
                          {importStatus.details.length > 8 && (
                            <li className="text-xs">... and {importStatus.details.length - 8} more files</li>
                          )}
                        </ul>
                      </div>
                    ) : (
                      <ul className="list-disc list-inside text-red-300">
                        {importStatus.details.map((detail, index) => (
                          <li key={index}>{detail}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragOver
                  ? 'border-blue-400 bg-blue-400 bg-opacity-10'
                  : importing
                  ? 'border-yellow-400 bg-yellow-400 bg-opacity-10'
                  : 'border-gray-600 hover:border-gray-500'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {importing ? (
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-yellow-300 mb-2">Importing project...</p>
                  <p className="text-yellow-200 text-sm">Processing all files and saving to database</p>
                </div>
              ) : (
                <>
                  <SafeIcon icon={FiUpload} className="text-4xl text-gray-400 mb-4 mx-auto" />
                  <p className="text-gray-300 mb-2">
                    Drag and drop a project .zip file here
                  </p>
                  <p className="text-gray-500 text-sm mb-4">or</p>
                  <label className="inline-block bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded cursor-pointer transition-colors">
                    <input
                      type="file"
                      accept=".zip"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file);
                      }}
                      className="hidden"
                      disabled={importing}
                    />
                    Choose File
                  </label>
                </>
              )}
            </div>

            <div className="mt-4 text-sm text-gray-400">
              <p>• Projects should be exported as .zip files</p>
              <p>• Imported projects will be added to your project list</p>
              <p>• All files and folder structures will be preserved</p>
              <p>• ✅ <strong>Fixed:</strong> Now properly imports and saves all files to database</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ProjectImportExport;